import React, { useState, useEffect } from "react";
import { CommonDialog } from "../../../../components/common/CommonDialog";
import { DialogTitle, Stack } from "@mui/material";
import { FormButtonGroup, FormTextInput } from "../../../../components/form";
import { FormSelectCompleted } from "../../../../components/form/FormSelectCompleted";
import { useSessionContext } from "../../../../contexts/SessionContext";
import { PatchPatient } from "../../../../services/fhir/patient/PatchPatient";
import { toast } from "react-toastify";
import { useWatch } from "react-hook-form";

interface IProps {
    type: "add" | "edit";
    isOpen: boolean;
    handleClose: VoidFunction;
    display: any;
    control: any;
    handleSubmit: any;
}

export function OccupationDialog({
    type,
    isOpen,
    handleClose,
    display,
    control,
    handleSubmit,
}: IProps) {
    const { userPatient, fetchUser } = useSessionContext();
    const [showOtherField, setShowOtherField] = useState(false);

    const occupationValue = useWatch({
        control,
        name: "occupation",
    });

    useEffect(() => {
        if (occupationValue?.value === "Other") {
            setShowOtherField(true);
        } else {
            setShowOtherField(false);
        }
    }, [occupationValue]);

    const codesOccupation = [
        {
            value: "Employed",
            display: "Empregado(a)",
            code: "EMP",
        },
        {
            value: "Self-Employed",
            display: "Autônomo(a)",
            code: "SELF",
        },
        {
            value: "Public Servant",
            display: "Servidor(a) Público(a)",
            code: "PUBSERV",
        },
        {
            value: "Retired",
            display: "Aposentado(a)",
            code: "RET",
        },
        {
            value: "Student",
            display: "Estudante",
            code: "STU",
        },
        {
            value: "Unemployed",
            display: "Desempregado(a)",
            code: "UNEMP",
        },
        {
            value: "Homemaker",
            display: "Do Lar",
            code: "HOME",
        },
        {
            value: "Health Professional",
            display: "Profissional de Saúde",
            code: "HEALTH",
        },
        {
            value: "Education Professional",
            display: "Profissional de Educação",
            code: "EDU",
        },
        {
            value: "Technology Professional",
            display: "Profissional de Tecnologia",
            code: "TECH",
        },
        {
            value: "Business Owner",
            display: "Empresário(a)",
            code: "OWNER",
        },
        {
            value: "Other",
            display: "Outro",
            code: "OTH",
        },
    ];

    const auxDefault = codesOccupation?.find((el: any) => el.display === display);
    const isCustomOccupation = display && !auxDefault;

    useEffect(() => {
        if (isCustomOccupation) {
            setShowOtherField(true);
        }
    }, [isCustomOccupation]);

    function verifyPath(extensionOccupation: any) {
        if (extensionOccupation === undefined) {
            return "/extension";
        }
        if (extensionOccupation === -1) {
            return "/extension/-";
        } else {
            return `/extension/${extensionOccupation}`;
        }
    }

    const auxFindExtensionOccupation = userPatient?.extension?.findIndex(
        (el: any) =>
            el.url === "https://terminologia.saude.gov.br/fhir/ValueSet/BROcupacao"
    );

    const onSubmit = async (data: any) => {
        let finalDisplay = codesOccupation?.find(
            (el: any) => el.value === data.occupation?.value
        )?.display;

        if (data.occupation?.value === "Other") {
            if (!data.customOccupation || data.customOccupation.trim() === "") {
                toast.error("Por favor, especifique a profissão");
                return;
            }
            finalDisplay = data.customOccupation.trim();
        }

        const path = verifyPath(auxFindExtensionOccupation);
        const content = {
            url: "https://terminologia.saude.gov.br/fhir/ValueSet/BROcupacao",
            valueCodeableConcept: {
                coding: [
                    {
                        system: "https://terminologia.saude.gov.br/fhir/CodeSystem/BROcupacao",
                        code: data?.occupation?.code,
                        display: finalDisplay,
                    },
                ],
            },
        };

        try {
            await PatchPatient({
                patientId: userPatient?.id,
                operation: type === "add" ? "add" : "replace",
                path,
                content: auxFindExtensionOccupation === undefined ? [content] : content,
            });
            await fetchUser();
            toast.success(
                `Profissão ${type === "add" ? "adicionada" : "atualizada"
                } com sucesso!`
            );
            handleClose();
        } catch (err) {
            console.log("err", err);
        }
    };

    return (
        <CommonDialog isOpen={isOpen} handleClose={handleClose}>
            <DialogTitle>
                {type === "add" ? "Adicionar" : "Atualizar"} Profissão
                <Stack marginY={2} spacing={2}>
                    <FormSelectCompleted
                        defaultValue={isCustomOccupation ? codesOccupation.find(el => el.code === "OTH") : auxDefault}
                        name="occupation"
                        label=""
                        control={control}
                        selectOptions={codesOccupation}
                        required
                    />

                    {showOtherField && (
                        <FormTextInput
                            name="customOccupation"
                            control={control}
                            label="Especifique a profissão"
                            placeholder="Digite sua profissão"
                            defaultValue={isCustomOccupation ? display : ""}
                            required={showOtherField}
                            autoFocus
                        />
                    )}
                </Stack>
                <FormButtonGroup
                    goBackButtonText="Cancelar"
                    nextButtonText="Salvar"
                    onGoBackButtonClick={handleClose}
                    onNextButtonClick={handleSubmit(onSubmit)}
                />
            </DialogTitle>
        </CommonDialog>
    );
}

